<template>
  <div class="page-wrap">

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div>
        <h1 class="page-title">Transactions List</h1>
        <span class="page-subtitle">Manage and track all transactions</span>
      </div>
      <div class="page-header__right">
        <button class="btn-export" @click="exportToCSV" v-if="transitions && transitions.length" title="Export to CSV">
          <i class="fa fa-download"></i> Export CSV
        </button>
      </div>
    </div>

    <!-- ── Filters ── -->
    <div class="filter-panel">
      <div class="filter-panel__row">

        <div class="filter-field filter-field--grow">
          <label class="filter-label">Search</label>
          <div class="input-icon-wrap">
            <i class="fa fa-search input-icon"></i>
            <input
                placeholder="Search by user ID or name..."
                v-model="inputFilters.q"
                class="dd-input dd-input--icon"
                v-on:keyup.enter="filter"
            />
          </div>
        </div>

        <div class="filter-field filter-field--sm">
          <label class="filter-label">Transaction Type</label>
          <select v-model="inputFilters.payment_method" class="dd-input">
            <option value="">All</option>
            <option value="exchange">Exchange</option>
            <option value="balance">Balance</option>
            <option value="tap">Tap</option>
          </select>
        </div>

        <div class="filter-field">
          <label class="filter-label">Date Range</label>
          <date-picker
              v-model="inputFilters.date"
              locale="en"
              format="YYYY-MM-DD"
              range
              clearable
              color="#f42f4b"
          />
        </div>

        <div class="filter-field filter-field--action">
          <label class="filter-label" style="visibility:hidden">Go</label>
          <button class="btn-filter" @click="filter">
            <i class="fa fa-search"></i> Filter
          </button>
        </div>

      </div>
    </div>

    <!-- ── Table ── -->
    <div class="table-panel">
      <div class="table-responsive-wrap">
        <table class="dd-table">
          <thead>
          <tr>
            <th>ID</th>
            <th>Cart / Gift</th>
            <th>Customer</th>
            <th>Amount</th>
            <th>Status</th>
            <th>Payment Method</th>
            <th>Action</th>
            <th>Date</th>
            <th>Comment</th>
          </tr>
          </thead>
          <tbody>
          <tr
              v-for="transaction in transitions"
              :key="transaction.id"
              class="dd-table__row"
          >
            <td><span class="order-id">#{{ transaction.id }}</span></td>
            <td>
              <div class="ref-ids">
                  <span class="ref-id" v-if="transaction.cart?.id">
                    <i class="fa fa-shopping-cart"></i> {{ transaction.cart.id }}
                  </span>
                <span class="ref-id" v-if="transaction.meta?.gift_id">
                    <i class="fa fa-gift"></i> {{ transaction.meta.gift_id }}
                  </span>
                <span class="text-muted" v-if="!transaction.cart?.id && !transaction.meta?.gift_id">—</span>
              </div>
            </td>
            <td>
              <div class="customer-info">
                <div class="customer-avatar">
                  {{ (transaction.user.full_name || '?')[0].toUpperCase() }}
                </div>
                <div>
                  <div class="customer-name">{{ transaction.user.full_name }}</div>
                  <div class="customer-id">ID: {{ transaction.user.id }}</div>
                </div>
              </div>
            </td>
            <td>
                <span :class="['amount-val', transaction.amount > 0 ? 'amount-val--pos' : 'amount-val--neg']">
                  {{ transaction.amount > 0 ? '+' : '' }}{{ transaction.amount }} {{ transaction.currency }}
                </span>
            </td>
            <td v-html="getStatusBadge(transaction.status)"></td>
            <td>
                <span class="method-badge" v-if="transaction.payment_method">
                  <i class="fa fa-credit-card"></i> {{ transaction.payment_method }}
                </span>
              <span class="text-muted" v-else>—</span>
            </td>
            <td>
                <span :class="['action-badge', transaction.type === 'Deposit' ? 'action-badge--deposit' : 'action-badge--withdraw']">
                  <i :class="transaction.type === 'Deposit' ? 'fa fa-arrow-down' : 'fa fa-arrow-up'"></i>
                  {{ transaction.type }}
                </span>
            </td>
            <td class="date-cell">{{ formatDate(transaction.created_at) || '—' }}</td>
            <td>
              <span
                  v-if="transaction.comment"
                  :id="`comment-${transaction.id}`"
                  class="comment-text"
              >{{ transaction.comment }}</span>
                          <b-tooltip
                              :target="`comment-${transaction.id}`"
                              triggers="hover"
                              placement="top"
                          >{{ transaction.comment }}</b-tooltip>
            </td>
          </tr>

          <tr v-if="transitions && transitions.length === 0">
            <td colspan="9" class="empty-state">
              <i class="fa fa-exchange"></i>
              <div>No transactions found</div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-wrap">
        <div class="pagination-info">
          Showing {{ transitions ? transitions.length : 0 }} of {{ totalItems }} transactions
        </div>
        <b-pagination
            v-model="page"
            :total-rows="totalItems"
            :per-page="perPage"
            last-number
            class="dd-pagination"
        ></b-pagination>
      </div>
    </div>

  </div>
</template>

<script>
import { config } from "../helpers";

export default {
  name: "Transactions",
  data() {
    return {
      url: "",
      transitions: [],
      inputFilters: {
        q: null,
        payment_method: "",
        date: [],
      },
      isFiltered: false,
      filters: {},
      page: 1,
      lastPage: 1,
      totalItems: 1,
      perPage: 1,
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT") + "/admin/transactions";
    this.loadData();
  },

  methods: {
    loadData() {
      let myData = { page: this.page, sort_by: "created_at" };
      if (this.isFiltered) myData = { ...myData, ...this.filters };
      axios.get(this.url, { params: myData }).then((r) => {
        this.transitions = r.data.data;
        this.page        = r.data.meta.current_page;
        this.lastPage    = r.data.meta.last_page;
        this.totalItems  = r.data.meta.total;
        this.perPage     = r.data.meta.per_page;
      });
    },

    filter() {
      this.page       = 1;
      this.isFiltered = true;
      const filters   = {};
      if (this.inputFilters.q)              filters.q              = this.inputFilters.q;
      if (this.inputFilters.payment_method) filters.payment_method = this.inputFilters.payment_method;
      if (this.inputFilters.date[0]) {
        filters.date = { start: this.inputFilters.date[0], end: this.inputFilters.date[1] };
      }
      this.filters = filters;
      this.loadData();
    },

    exportToCSV() {
      const myData = this.isFiltered ? { ...this.filters } : {};
      axios.get(this.url + "/export", { params: myData }).then((r) => {
        const url  = window.URL.createObjectURL(new Blob([r.data]));
        const link = document.createElement("a");
        link.href  = url;
        link.setAttribute("download", "transactions.csv");
        document.body.appendChild(link);
        link.click();
        link.remove();
      });
    },

    formatDate(date) {
      if (!date) return null;
      const d = new Date(date);
      return `${d.getFullYear()}-${d.getMonth()+1}-${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2,"0")}`;
    },

    getStatusBadge(status) {
      const map = {
        success: ["status-badge status-badge--green", "Success"],
        pending: ["status-badge status-badge--amber", "Pending"],
        failed:  ["status-badge status-badge--red",   "Failed"],
      };
      const [cls, label] = map[status?.toLowerCase()] ?? ["status-badge status-badge--gray", status];
      return `<span class="${cls}">${label}</span>`;
    },
  },

  watch: {
    page() { this.loadData(); },
  },
};
</script>

<style scoped>
.page-wrap    { display: flex; flex-direction: column; gap: 16px; }

.page-header  { display: flex; align-items: flex-end; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
.page-title   { font-size: 20px; font-weight: 500; color: var(--text-1); margin: 0 0 2px; }
.page-subtitle { font-size: 12px; color: var(--text-3); }

.btn-export { height: 36px; padding: 0 14px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: border-color .15s, color .15s; }
.btn-export:hover { border-color: var(--accent); color: var(--accent); }

/* ── Filter ── */
.filter-panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; padding: 1.1rem 1.25rem; }
.filter-panel__row { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.filter-field         { display: flex; flex-direction: column; min-width: 160px; }
.filter-field--grow   { flex: 1; }
.filter-field--sm     { min-width: 130px; max-width: 160px; }
.filter-field--action { min-width: auto; }
.filter-label { font-size: 11px; font-weight: 500; color: var(--text-3); text-transform: uppercase; letter-spacing: .4px; margin-bottom: 5px; }

.dd-input { height: 36px; background: var(--bg-base); border: 1px solid var(--border); border-radius: 8px; color: var(--text-1); font-size: 13px; padding: 0 10px; outline: none; width: 100%; transition: border-color .15s; }
.dd-input:focus { border-color: var(--accent); }
.dd-input::placeholder { color: var(--text-3); }
.input-icon-wrap { position: relative; }
.input-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-3); font-size: 13px; }
.dd-input--icon { padding-left: 32px; }

.btn-filter { height: 36px; padding: 0 16px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity .15s; }
.btn-filter:hover { opacity: .88; }

/* ── Table ── */
.table-panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; }
.table-responsive-wrap { overflow-x: auto; }
.dd-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.dd-table thead tr { border-bottom: 1px solid var(--border); }
.dd-table th { padding: 10px 14px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; color: var(--text-3); white-space: nowrap; text-align: left; }
.dd-table td { padding: 12px 14px; color: var(--text-2); border-bottom: 1px solid var(--border); vertical-align: middle; }
.dd-table__row:hover td { background: var(--bg-hover); }
.dd-table tr:last-child td { border-bottom: none; }

.order-id { font-weight: 500; color: var(--text-1); }

/* Ref IDs */
.ref-ids { display: flex; flex-direction: column; gap: 3px; }
.ref-id  { font-size: 12px; color: var(--text-2); display: flex; align-items: center; gap: 5px; }
.ref-id i { color: var(--text-3); font-size: 11px; }
.text-muted { color: var(--text-3); font-size: 12px; }

/* Customer */
.customer-info   { display: flex; align-items: center; gap: 9px; }
.customer-avatar { width: 28px; height: 28px; background: rgba(244,47,75,.12); color: var(--accent); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 600; flex-shrink: 0; }
.customer-name   { font-size: 13px; font-weight: 500; color: var(--text-1); }
.customer-id     { font-size: 11px; color: var(--text-3); }

/* Amount */
.amount-val       { font-weight: 600; font-size: 14px; }
.amount-val--pos  { color: #22c55e; }
.amount-val--neg  { color: #f42f4b; }

/* Method badge */
.method-badge { display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; border-radius: 20px; font-size: 11px; font-weight: 500; background: rgba(129,140,248,.12); color: #818cf8; border: 1px solid rgba(129,140,248,.25); white-space: nowrap; }

/* Action badge */
.action-badge { display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; border-radius: 20px; font-size: 11px; font-weight: 500; white-space: nowrap; }
.action-badge--deposit  { background: rgba(34,197,94,.12);  color: #22c55e; border: 1px solid rgba(34,197,94,.25); }
.action-badge--withdraw { background: rgba(244,47,75,.12);  color: #f42f4b; border: 1px solid rgba(244,47,75,.25); }

.date-cell    { font-size: 12px; color: var(--text-3); white-space: nowrap; }
.comment-text { font-size: 12px; color: var(--text-2); max-width: 160px; display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* Empty */
.empty-state { text-align: center; padding: 3rem 1rem !important; color: var(--text-3); }
.empty-state i { font-size: 32px; display: block; margin-bottom: 8px; }

/* Pagination */
.pagination-wrap { display: flex; align-items: center; justify-content: space-between; padding: .75rem 1.25rem; border-top: 1px solid var(--border); flex-wrap: wrap; gap: 8px; }
.pagination-info { font-size: 12px; color: var(--text-3); }
.dd-pagination >>> .page-link { background: var(--bg-hover); border-color: var(--border); color: var(--text-2); font-size: 13px; }
.dd-pagination >>> .page-item.active .page-link { background: var(--accent); border-color: var(--accent); color: #fff; }
.dd-pagination >>> .page-link:hover { background: var(--bg-hover); border-color: var(--accent); color: var(--accent); }

@media (max-width: 768px) {
  .filter-panel__row { flex-direction: column; }
  .filter-field, .filter-field--sm, .filter-field--grow { width: 100%; max-width: 100%; }
}
.comment-text {
  font-size: 12px;
  color: var(--text-2);
  max-width: 160px;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: help;
  border-bottom: 1px dashed var(--border-2);
}
</style>